import { Token } from "@0xsquid/squid-types";
import { isValidClassicAddress, isValidXAddress } from "ripple-address-codec";
import { CHAIN_IDS } from "../../core/constants";
import { TrustLineAsset } from "../../core/types/tokens";
import {
  XrplNetwork,
  XrplPaymentTx,
  XrplTransactionFlag,
  XrplTransactionType,
  XrplTrustSetTx,
} from "../../core/types/xrpl";

/**
 * Validates that a given address is a valid XRPL address.
 *
 * There are two types of address in XRPL:
 * - X-address (e.g XVfC9...pokKH)
 * - Classic address (e.g r4bA...WHCG)
 */
export function isXrplAddressValid(address: string | undefined): boolean {
  if (!address) return false;

  return isValidXAddress(address) || isValidClassicAddress(address);
}

export function buildXrplTrustSetTx({
  amount,
  sourceAddress,
  token,
}: {
  amount: string;
  sourceAddress: string;
  token: Token;
}): XrplTrustSetTx {
  const asset = parseXrplTokenAddress(token.address);

  if (!asset) {
    throw new Error("Invalid asset");
  }

  return {
    TransactionType: XrplTransactionType.TRUST_SET,
    Flags: XrplTransactionFlag.tfSetNoRipple,
    Account: sourceAddress,
    LimitAmount: {
      currency: asset.code,
      issuer: asset.issuer,
      value: amount,
    },
  };
}

export function getXrplNetwork(chainId: string): XrplNetwork | null {
  switch (chainId) {
    case CHAIN_IDS.XRPL:
      return XrplNetwork.MAINNET;
    case CHAIN_IDS.XRPL_TESTNET:
      return XrplNetwork.TESTNET;
    default:
      return null;
  }
}

export function parseXrplPaymentTx(data: unknown): XrplPaymentTx | null {
  try {
    const asPaymentTx = data as XrplPaymentTx;

    if (
      Array.isArray(asPaymentTx.Memos) &&
      isXrplAddressValid(asPaymentTx.Account) &&
      isXrplAddressValid(asPaymentTx.Destination)
    ) {
      return asPaymentTx;
    }

    throw new Error("Could not parse payment transaction");
  } catch (error) {
    throw new Error("Could not parse payment transaction");
  }
}

export function parseXrplTokenAddress(address: string): TrustLineAsset | null {
  const [code, issuer] = address.split(".");

  if (!code || !issuer) {
    return null;
  }

  return {
    code,
    issuer,
  };
}
